/**
 * Host-side lobby + room lock.
 *
 * Both surfaces that show waiting people — the "Lobby" pill over the video
 * grid and the Participants panel's lobby section — used to render hardcoded
 * arrays ("Alice Johnson", "sw / 1 unconfirmed user"), so admitting did
 * nothing but splice a local list. This hook is the single real source for
 * both.
 *
 * Liveness: one fetch on mount, then the `lobby:updated` and
 * `room:lock_changed` broadcasts that the backend fans into `room:<meetingId>`
 * whenever anyone knocks or the lock flips. The host is already on that
 * channel, so no polling is needed on this side. (The *waiting* joiner is a
 * different story — it holds no room credentials yet, so it cannot be on the
 * channel and keeps its existing 2s poll in VcApp.)
 *
 * Non-hosts get an inert hook: the endpoints are host-only (403), so there is
 * nothing to fetch and nothing to show.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { lobbyApi, type LobbyRequest, type LobbyRequestList } from '@/features/meetings/services/meetings.api'
import type { PhoenixEventHandler } from './usePhoenixChannel'
import { vclog } from './vc-log'

export type LobbyState = {
  /** People waiting for admission. Empty for non-hosts. */
  requests: LobbyRequest[]
  /** Server truth for the room lock, kept in sync by `room:lock_changed`. */
  locked: boolean
  /** True while a lock/unlock round-trip is in flight. */
  lockPending: boolean
  admit: (requestId: string) => void
  deny: (requestId: string) => void
  admitAll: () => void
  setLocked: (locked: boolean) => void
}

type Args = {
  meetingId: string | null
  isHost: boolean
  subscribe: (event: string, handler: PhoenixEventHandler) => () => void
}

export function useLobby({ meetingId, isHost, subscribe }: Args): LobbyState {
  const [requests, setRequests] = useState<LobbyRequest[]>([])
  // A room starts unlocked; the first fetch and any broadcast correct this.
  const [locked, setLockedState] = useState(false)
  const [lockPending, setLockPending] = useState(false)
  // Rows with a decision in flight. A `lobby:updated` broadcast triggered by
  // someone else knocking can land before the server has processed our admit,
  // and its list still contains the row we just removed — which would put it
  // back and make the click look like it did nothing.
  const decidingRef = useRef<Set<string>>(new Set())

  const withoutDeciding = useCallback(
    (list: LobbyRequest[]) => list.filter((r) => !decidingRef.current.has(r.id)),
    [],
  )

  // Admit/deny send this id back, and the server resolves the room from it, so
  // it has to carry the room prefix. Both producers emit it that way; this
  // normalises anyway, because when they disagreed the symptom was baffling —
  // the row reappeared and the next click 403'd, so admitting took several
  // attempts rather than failing outright.
  const withRoom = useCallback(
    (list: LobbyRequest[]) =>
      meetingId
        ? list.map((r) => (r.id.includes('~') ? r : { ...r, id: `${meetingId}~${r.id}` }))
        : list,
    [meetingId],
  )

  const refresh = useCallback(() => {
    if (!meetingId || !isHost) return
    lobbyApi
      .listRequests(meetingId)
      .then((res: LobbyRequestList) => {
        setRequests(withoutDeciding(withRoom(res.requests ?? [])))
        if (typeof res.locked === 'boolean') setLockedState(res.locked)
      })
      .catch(() => {
        // 403 for a non-host, or a blip. The broadcast will correct us.
      })
  }, [meetingId, isHost, withoutDeciding, withRoom])

  useEffect(() => {
    refresh()
  }, [refresh])

  /**
   * Re-read the list on a slow tick, as well as on broadcasts.
   *
   * Everything else here is event-driven: `lobby:updated` arrives when someone
   * knocks or is decided about. A joiner who simply LEAVES produces no event at
   * all — they stop polling and nothing happens — so without a periodic read
   * their row would sit in the host's list until the next unrelated knock.
   *
   * The read is also what performs the server-side sweep: the coordinator drops
   * requests nobody has heard from when the pending list is asked for, rather
   * than on a timer of its own (a timer message would cancel the room's idle
   * timeout and leak the process). So this tick is not merely a refresh, it is
   * the thing that collects the rubbish.
   *
   * Fifteen seconds against a three-minute staleness limit: slow enough to be
   * nothing on a room with no one waiting, quick enough that a host is not left
   * looking at a name for long after its owner has gone.
   */
  useEffect(() => {
    if (!meetingId || !isHost) return
    const id = window.setInterval(refresh, 15_000)
    return () => window.clearInterval(id)
  }, [meetingId, isHost, refresh])

  // Live updates. `lobby:updated` carries the full pending list, so it
  // replaces rather than patches — no reconciliation, no drift.
  useEffect(() => {
    if (!isHost) return

    const offUpdated = subscribe('lobby:updated', (payload) => {
      const next = Array.isArray(payload.requests) ? (payload.requests as LobbyRequest[]) : []
      vclog('presence', 'lobby updated', { waiting: next.length })
      setRequests(withoutDeciding(withRoom(next)))
    })

    const offLock = subscribe('room:lock_changed', (payload) => {
      if (typeof payload.locked === 'boolean') {
        vclog('presence', 'room lock changed', { locked: payload.locked })
        setLockedState(payload.locked)
      }
    })

    return () => {
      offUpdated()
      offLock()
    }
  }, [subscribe, isHost, withoutDeciding, withRoom])

  // Optimistic: drop the row immediately so the list does not sit there
  // looking unresponsive. The broadcast that follows is authoritative, and a
  // failed call is repaired by `refresh`.
  const decide = useCallback(
    (requestId: string, action: 'admit' | 'deny') => {
      if (!meetingId || decidingRef.current.has(requestId)) return
      decidingRef.current.add(requestId)
      setRequests((prev) => prev.filter((r) => r.id !== requestId))
      lobbyApi[action](meetingId, requestId)
        .catch(() => {
          // Genuine failure — let it come back so the host can retry.
          decidingRef.current.delete(requestId)
          refresh()
        })
        .then(() => {
          // Keep it suppressed briefly after success: the authoritative
          // broadcast may still be in flight behind a stale one.
          setTimeout(() => decidingRef.current.delete(requestId), 3000)
        })
    },
    [meetingId, refresh],
  )

  const admit = useCallback((id: string) => decide(id, 'admit'), [decide])
  const deny = useCallback((id: string) => decide(id, 'deny'), [decide])

  const admitAll = useCallback(() => {
    if (!meetingId) return
    const pending = requests
    pending.forEach((r) => decidingRef.current.add(r.id))
    setRequests([])
    Promise.all(pending.map((r) => lobbyApi.admit(meetingId, r.id).catch(() => null))).then(() => {
      setTimeout(() => {
        pending.forEach((r) => decidingRef.current.delete(r.id))
        refresh()
      }, 3000)
    })
  }, [meetingId, requests, refresh])

  const setLocked = useCallback(
    (next: boolean) => {
      if (!meetingId) return
      setLockPending(true)
      setLockedState(next) // optimistic; rolled back below on failure
      lobbyApi
        .setLock(meetingId, next)
        .then((res) => setLockedState(res.locked))
        .catch(() => setLockedState(!next))
        .finally(() => setLockPending(false))
    },
    [meetingId],
  )

  return { requests, locked, lockPending, admit, deny, admitAll, setLocked }
}
